/**
 * Social Media Tracker — Scoring-/Malus-/Pacing-Helper (kein UI, kein Firestore).
 * Basis-Monatsziel: 1.000 Punkte; Urlaub skaliert via Lucy-Formel.
 */

export type SocialMediaLogType = 'story' | 'post' | 'reel';

export type PacingAmpel = 'green' | 'yellow' | 'orange' | 'red';

export const SOCIAL_MEDIA_POINTS: Record<SocialMediaLogType, number> = {
    story: 10,
    post: 25,
    reel: 50,
};

/** Soll-Stückzahlen pro Monat (bei 0 Urlaubstagen) */
export const SOCIAL_MEDIA_MONTHLY_TARGETS: Record<SocialMediaLogType, number> = {
    story: 60,
    post: 8,
    reel: 4,
};

export const SOCIAL_MEDIA_MONTHLY_TARGET_POINTS = 1000;
export const SOCIAL_MEDIA_VACATION_BASE_DAYS = 20;

/** Christian — Display + Billing exempt */
export const SOCIAL_MEDIA_MALUS_EXEMPT_EMAIL = 'erfolg@friseur-coaching.de';
/** Martina — nur Billing exempt (UI zeigt Malus trotzdem) */
export const SOCIAL_MEDIA_BILLING_EXEMPT_EMAIL = 'coaching@preisexpert.info';

export const isSocialMediaLogType = (value: unknown): value is SocialMediaLogType =>
    value === 'story' || value === 'post' || value === 'reel';

export const pointsFor = (type: SocialMediaLogType, count: number = 1): number => {
    const safeCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    return SOCIAL_MEDIA_POINTS[type] * safeCount;
};

export const monthKeyFromDate = (dateIso: string): string => {
    const trimmed = String(dateIso || '').trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}`;
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
};

export const todayIsoLocal = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const currentMonthKey = (): string => monthKeyFromDate(todayIsoLocal());

export const previousMonthKey = (monthKey?: string): string => {
    const key = String(monthKey || currentMonthKey()).trim();
    const match = key.match(/^(\d{4})-(\d{2})$/);
    if (!match) return currentMonthKey();
    const y = Number(match[1]);
    const m = Number(match[2]);
    const d = new Date(y, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Urlaubstage clamp 0…20 */
export const clampVacationDays = (raw: unknown): number => {
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(SOCIAL_MEDIA_VACATION_BASE_DAYS, Math.floor(n));
};

/**
 * Lucy Pro-Rata:
 * targetPoints = 1000 * ((20 - vacationDays) / 20)
 */
export const targetPointsFromVacation = (vacationDays: number = 0): number => {
    const days = clampVacationDays(vacationDays);
    return (
        SOCIAL_MEDIA_MONTHLY_TARGET_POINTS *
        ((SOCIAL_MEDIA_VACATION_BASE_DAYS - days) / SOCIAL_MEDIA_VACATION_BASE_DAYS)
    );
};

/** Zielerreichung % gegen dynamisches Soll */
export const achievementPercent = (
    totalPoints: number,
    targetPoints: number = SOCIAL_MEDIA_MONTHLY_TARGET_POINTS
): number => {
    const target = Number.isFinite(targetPoints) && targetPoints > 0 ? targetPoints : 0;
    if (target <= 0) return Number.isFinite(totalPoints) && totalPoints > 0 ? 100 : 0;
    if (!Number.isFinite(totalPoints) || totalPoints <= 0) return 0;
    return (totalPoints / target) * 100;
};

/**
 * Malus-Staffelung auf Ziel-%.
 * isExempt → immer 0% (für Display-Exempt / Billing-Exempt je nach Aufruf)
 */
export const malusRate = (percent: number, isExemptFromMalus: boolean = false): number => {
    if (isExemptFromMalus) return 0;
    const p = Number.isFinite(percent) ? percent : 0;
    if (p >= 100) return 0;
    if (p >= 80) return 3;
    if (p >= 60) return 6;
    return 10;
};

/** Christian: Display + Billing */
export const isMalusExemptEmail = (email?: string | null): boolean =>
    String(email || '').trim().toLowerCase() === SOCIAL_MEDIA_MALUS_EXEMPT_EMAIL;

/** Christian + Martina: kein €-Abzug in der Abrechnung */
export const isBillingMalusExemptEmail = (email?: string | null): boolean => {
    const e = String(email || '').trim().toLowerCase();
    return e === SOCIAL_MEDIA_MALUS_EXEMPT_EMAIL || e === SOCIAL_MEDIA_BILLING_EXEMPT_EMAIL;
};

/** Martina (optisch Malus zeigen, Billing 0) */
export const isMartinaEmail = (email?: string | null): boolean =>
    String(email || '').trim().toLowerCase() === SOCIAL_MEDIA_BILLING_EXEMPT_EMAIL;

export const isWorkdayDate = (d: Date): boolean => {
    const wd = d.getDay();
    return wd !== 0 && wd !== 6;
};

/** Arbeitstage Mo–Fr im Monat YYYY-MM */
export const countWorkdaysInMonth = (monthKey: string): number => {
    const match = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
    if (!match) return 0;
    const y = Number(match[1]);
    const m = Number(match[2]);
    const last = new Date(y, m, 0).getDate();
    let n = 0;
    for (let day = 1; day <= last; day++) {
        if (isWorkdayDate(new Date(y, m - 1, day))) n++;
    }
    return n;
};

/** Verstrichene Arbeitstage Mo–Fr im Monat bis asOf (inkl., max. Monatsende) */
export const countWorkdaysElapsedInMonth = (
    monthKey: string,
    asOf: Date = new Date()
): number => {
    const match = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
    if (!match) return 0;
    const y = Number(match[1]);
    const m = Number(match[2]);
    const last = new Date(y, m, 0).getDate();
    const asOfDay =
        asOf.getFullYear() === y && asOf.getMonth() + 1 === m
            ? Math.min(asOf.getDate(), last)
            : asOf.getFullYear() > y || (asOf.getFullYear() === y && asOf.getMonth() + 1 > m)
              ? last
              : 0;
    let n = 0;
    for (let day = 1; day <= asOfDay; day++) {
        if (isWorkdayDate(new Date(y, m - 1, day))) n++;
    }
    return n;
};

/** Kumuliertes Soll bis heute (Kalender Mo–Fr; Urlaub nur über targetPoints) */
export const expectedPointsToDate = (
    targetPoints: number,
    monthKey: string,
    asOf: Date = new Date()
): number => {
    const total = countWorkdaysInMonth(monthKey);
    const elapsed = countWorkdaysElapsedInMonth(monthKey, asOf);
    if (total <= 0 || !(targetPoints > 0)) return 0;
    return targetPoints * (elapsed / total);
};

/** Kalendertage im Monat YYYY-MM */
export const calendarDaysInMonth = (monthKey: string): number => {
    const match = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
    if (!match) return 0;
    const y = Number(match[1]);
    const m = Number(match[2]);
    return new Date(y, m, 0).getDate();
};

/**
 * Kumuliertes Kalender-Pacing-Soll bis heute:
 * calendarPacingSoll = (monatsSoll / tageImMonat) * aktuellerTag
 */
export const calendarPacingSoll = (
    monatsSoll: number,
    monthKey: string,
    asOf: Date = new Date()
): number => {
    const days = calendarDaysInMonth(monthKey);
    if (days <= 0 || !(monatsSoll > 0)) return 0;
    const match = String(monthKey || '').match(/^(\d{4})-(\d{2})$/);
    if (!match) return 0;
    const y = Number(match[1]);
    const m = Number(match[2]);
    let dayNum = 0;
    if (asOf.getFullYear() === y && asOf.getMonth() + 1 === m) {
        dayNum = Math.min(Math.max(1, asOf.getDate()), days);
    } else if (
        asOf.getFullYear() > y ||
        (asOf.getFullYear() === y && asOf.getMonth() + 1 > m)
    ) {
        dayNum = days;
    } else {
        dayNum = 0;
    }
    if (dayNum <= 0) return 0;
    return (monatsSoll / days) * dayNum;
};

/**
 * Ampel gegen maßgebliches Pacing-Soll:
 * >=100 % grün · 50–99 % gelb/orange · <50 % rot
 */
export const scopePacingAmpel = (
    istPunkte: number,
    pacingSoll: number
): PacingAmpel => {
    const ist = Number.isFinite(istPunkte) ? istPunkte : 0;
    if (!(pacingSoll > 0)) {
        return ist > 0 ? 'yellow' : 'red';
    }
    const ratio = (ist / pacingSoll) * 100;
    if (ratio >= 100) return 'green';
    if (ratio >= 50) return ratio >= 80 ? 'yellow' : 'orange';
    return 'red';
};

export const pacingAmpel = (
    currentPoints: number,
    expectedToDate: number
): PacingAmpel => {
    // Gleiche Stufen wie scopePacingAmpel (Pacing-Erfüllung)
    return scopePacingAmpel(currentPoints, expectedToDate);
};

/**
 * Ampel strikt nach Monats-Ziel-% (nicht Flow-Pacing).
 * 0–49 % = rot · 50–99 % = gelb/orange · 100 %+ = grün.
 * Wirksamer Punktabzug oder 0 % Erfüllung → niemals grün.
 */
export const achievementAmpel = (
    percent: number,
    malusRatePct: number = 0
): PacingAmpel => {
    const p = Number.isFinite(percent) ? percent : 0;
    const malus = Number.isFinite(malusRatePct) ? malusRatePct : 0;
    if (p <= 0) return 'red';
    if (p >= 100 && malus <= 0) return 'green';
    // 50–99 % (inkl. Malus-Zonen): nie grün
    if (p >= 50) {
        if (p >= 80) return 'yellow'; // 3 %-Abzug-Zone
        return 'orange'; // 6 % / 10 %-Zone
    }
    return 'red';
};

export const PACING_AMPEL_COLOR: Record<PacingAmpel, string> = {
    green: '#22c55e',
    yellow: '#eab308',
    orange: '#f97316',
    red: '#ef4444',
};

/** Archiv-Dropdown: Systemstart PEPulse */
export const PEPULSE_ARCHIVE_START_MONTH = '2026-08';

/** Monatsoptionen von max(Start, …) bis aktueller Monat, ohne Zukunft. */
export const buildPepulseArchiveMonths = (
    asOf: Date = new Date(),
    startMonth: string = PEPULSE_ARCHIVE_START_MONTH
): string[] => {
    const start = String(startMonth || PEPULSE_ARCHIVE_START_MONTH);
    const end = monthKeyFromDate(
        `${asOf.getFullYear()}-${String(asOf.getMonth() + 1).padStart(2, '0')}-01`
    );
    if (!end || end < start) return [start];
    const opts: string[] = [];
    let y = Number(end.slice(0, 4));
    let m = Number(end.slice(5, 7));
    while (true) {
        const key = `${y}-${String(m).padStart(2, '0')}`;
        if (key < start) break;
        opts.push(key);
        m -= 1;
        if (m < 1) {
            m = 12;
            y -= 1;
        }
        if (opts.length > 36) break;
    }
    return opts;
};

/** Anzeige-Malus-% (Christian exempt, Martina NICHT) */
export const displayMalusRate = (percent: number, email?: string | null): number =>
    malusRate(percent, isMalusExemptEmail(email));

/** Billing-Malus-% (Christian + Martina = 0) */
export const billingMalusRate = (percent: number, email?: string | null): number =>
    malusRate(percent, isBillingMalusExemptEmail(email));

/**
 * Effektiver €-Malus für Abrechnung/Auszahlung.
 * isDeductionActive=false → immer 0 € (Testphasen-Schalter); %-Anzeige bleibt separat.
 */
export const billingMalusEuro = (
    baseEuro: number,
    percent: number,
    email?: string | null,
    isDeductionActive: boolean = true
): number => {
    if (!isDeductionActive) return 0;
    const base = Number(baseEuro) || 0;
    const rate = billingMalusRate(percent, email);
    if (rate <= 0 || base <= 0) return 0;
    return -((base * rate) / 100);
};

export type SocialMediaTypeBreakdown = {
    type: SocialMediaLogType;
    count: number;
    points: number;
    targetCount: number;
    targetPoints: number;
};

export type SocialMediaMonthlyStats = {
    month: string;
    userId: string;
    byType: Record<SocialMediaLogType, SocialMediaTypeBreakdown>;
    totalPoints: number;
    targetPoints: number;
    vacationDays: number;
    percent: number;
    /** Optischer Malus (Martina kann > 0 sein) */
    malusRate: number;
    /** Ob Display-Exempt (Christian) */
    isExemptFromMalus: boolean;
    pacingAmpel: PacingAmpel;
    expectedToDate: number;
};

export type SocialMediaLogLike = {
    type: SocialMediaLogType;
    count?: number;
    points?: number;
};

export const computeMonthlyStats = (
    logs: SocialMediaLogLike[],
    opts: {
        userId: string;
        month: string;
        vacationDays?: number;
        email?: string | null;
        isExemptFromMalus?: boolean;
        asOf?: Date;
    }
): SocialMediaMonthlyStats => {
    const vacationDays = clampVacationDays(opts.vacationDays);
    const targetPoints = targetPointsFromVacation(vacationDays);
    const displayExempt =
        opts.isExemptFromMalus === true || isMalusExemptEmail(opts.email);

    const byType = (['story', 'post', 'reel'] as SocialMediaLogType[]).reduce(
        (acc, type) => {
            acc[type] = {
                type,
                count: 0,
                points: 0,
                targetCount: SOCIAL_MEDIA_MONTHLY_TARGETS[type],
                targetPoints: SOCIAL_MEDIA_POINTS[type] * SOCIAL_MEDIA_MONTHLY_TARGETS[type],
            };
            return acc;
        },
        {} as Record<SocialMediaLogType, SocialMediaTypeBreakdown>
    );

    for (const log of logs || []) {
        if (!isSocialMediaLogType(log.type)) continue;
        const count =
            Number.isFinite(log.count) && (log.count as number) > 0
                ? Math.floor(log.count as number)
                : 1;
        const pts =
            Number.isFinite(log.points) && (log.points as number) >= 0
                ? Number(log.points)
                : pointsFor(log.type, count);
        byType[log.type].count += count;
        byType[log.type].points += pts;
    }

    const totalPoints =
        byType.story.points + byType.post.points + byType.reel.points;
    const percent = achievementPercent(totalPoints, targetPoints);
    const expected = expectedPointsToDate(targetPoints, opts.month, opts.asOf);
    const displayMalus = malusRate(percent, displayExempt);

    return {
        month: opts.month,
        userId: opts.userId,
        byType,
        totalPoints,
        targetPoints,
        vacationDays,
        percent,
        malusRate: displayMalus,
        isExemptFromMalus: displayExempt,
        pacingAmpel: achievementAmpel(percent, displayMalus),
        expectedToDate: expected,
    };
};
